import { useState, useEffect, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api';
import { SearchCombobox } from '@/components/SearchCombobox';

// ─── ServerSearchCombobox ───────────────────────────────────────────────────────
// A select-only typeahead backed by a debounced server search. Owns the debounce +
// query + loading plumbing on top of the generic SearchCombobox; callers supply the
// endpoint, how to pull rows out of the response, how to render/label a row, and
// receive the chosen row (or null when a prior selection is cleared by typing).
// EstimateCombobox / PurchaseOrderCombobox are thin adapters over this.

interface ServerSearchComboboxProps<TRow> {
  /** Selected row id, or null. Controlled by the parent (drives input styling + search gating). */
  value: number | null;
  /** Called with the chosen row, or null when the selection is cleared by typing. */
  onChange: (row: TRow | null) => void;
  /** React Query key prefix for this picker (e.g. 'estimate-picker'). */
  queryKey: string;
  /** Build the request URL for a (debounced) search term. */
  buildUrl: (q: string) => string;
  /** Pull the row array out of the API response (return [] when absent). */
  extract: (resp: unknown) => TRow[];
  getKey: (row: TRow) => number;
  /** Label shown in the input once a row is picked. */
  getLabel: (row: TRow) => string;
  renderResult: (row: TRow) => ReactNode;
  disabled?: boolean;
  placeholder?: string;
  promptMessage?: string;
  emptyMessage?: string;
}

export function ServerSearchCombobox<TRow>({
  value,
  onChange,
  queryKey,
  buildUrl,
  extract,
  getKey,
  getLabel,
  renderResult,
  disabled,
  placeholder,
  promptMessage,
  emptyMessage,
}: ServerSearchComboboxProps<TRow>) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  const trimmed = query.trim();

  // Debounce the trimmed value ~250ms so each keystroke doesn't fire a request.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(trimmed), 250);
    return () => clearTimeout(t);
  }, [trimmed]);

  // Only search while nothing is selected — once picked, the field shows the row's
  // label and re-searching would surface a dropdown of itself.
  const shouldFilter = value === null;

  const { data, isFetching } = useQuery({
    queryKey: [queryKey, debounced],
    queryFn: () => apiFetch<unknown>(buildUrl(debounced)),
    enabled: shouldFilter && debounced.length >= 1,
    staleTime: 15_000,
  });

  const results = shouldFilter ? extract(data) : [];
  // "Pending" = still waiting for the debounce to settle or the request to land.
  const loading = shouldFilter && trimmed.length >= 1 && (isFetching || debounced !== trimmed);

  function handleSelect(row: TRow) {
    onChange(row);
    setQuery(getLabel(row));
  }

  function handleQueryChange(q: string) {
    setQuery(q);
    // Typing drops any prior selection so a fresh pick can replace it.
    if (value !== null) onChange(null);
  }

  return (
    <SearchCombobox<TRow>
      value={value}
      query={query}
      onQueryChange={handleQueryChange}
      results={results}
      getKey={getKey}
      renderResult={renderResult}
      onSelect={handleSelect}
      placeholder={placeholder}
      disabled={disabled}
      loading={loading}
      promptMessage={promptMessage}
      emptyMessage={shouldFilter ? emptyMessage : undefined}
    />
  );
}
